import * as React from 'react';
import { useSelector } from 'react-redux';
import { Provider } from 'store/api/enums';
import { RECEIPTS_BEFORE_SCAN_COA_SKIPPED_KEY_PREFIX } from 'shared/constants/transactionsTourKeys';
import { entitySupportsDocumentScanning } from 'utils/captureTransactionsAccess';
import { getAuthUser } from 'store/auth/selectors';
import { getOrgOrganisations } from 'store/organisation/selectors';
import { getTaxRatesForConnection } from 'store/tax-rates/selectors';
import {
  PreferenceActions,
  PreferenceSections,
  usePreferences,
} from 'utils/preferences';
import { hasEntityProfileSection } from './EntityProfileDrawerContent';
import { resolveOrganisationTaxRatesForSetupCheck } from './transactions-tab/entity-transaction-helpers';

/** @deprecated Use {@link entitySupportsDocumentScanning} */
export const isCaptureScanPrerequisitesProvider = entitySupportsDocumentScanning;

type ReceiptsScanPrerequisitesOptions = {
  coaSkipped?: boolean;
  taxRatesFromStore?: { length: number } | null;
};

/**
 * Steps 1–3 of the "Before you scan" checklist (profile, tax rates, COA).
 * Matches {@link UploadTransactionsPanel} / `scanAreaEnabled` for receipts capture.
 */
export function areReceiptsScanPrerequisitesMetForOrganisation(
  organisation: { providerName?: string | null; organisationDetails?: { notes?: string }; chartOfAccounts?: unknown[]; taxRates?: unknown; taxRatesUnique?: unknown; hasTaxRates?: boolean } | undefined | null,
  options?: ReceiptsScanPrerequisitesOptions
): boolean {
  if (!organisation) {
    return false;
  }

  const providerName = organisation.providerName;
  const provider = providerName?.toLowerCase().trim();
  const isSupportedProvider = isCaptureScanPrerequisitesProvider(providerName);

  if (provider === Provider.EXCEL.toLowerCase() || !isSupportedProvider) {
    return true;
  }

  const organisationTaxRates = resolveOrganisationTaxRatesForSetupCheck(
    organisation as Parameters<typeof resolveOrganisationTaxRatesForSetupCheck>[0]
  );

  const hasTaxRates =
    organisationTaxRates.length > 0 ||
    (options?.taxRatesFromStore?.length ?? 0) > 0 ||
    Boolean(organisation.hasTaxRates);

  const hasChartOfAccounts =
    Array.isArray(organisation.chartOfAccounts) &&
    organisation.chartOfAccounts.length > 0;

  const profileStepCompleted = hasEntityProfileSection(
    organisation.organisationDetails?.notes
  );
  const coaStepCompleted = hasChartOfAccounts || (options?.coaSkipped ?? false);
  const taxRatesStepCompleted = hasTaxRates;

  return profileStepCompleted && taxRatesStepCompleted && coaStepCompleted;
}

export function useReceiptsScanPrerequisitesMet(
  organisationId?: string,
  organisation?: any,
  options?: { coaSkipped?: boolean }
): boolean {
  const organisations = useSelector(getOrgOrganisations);
  const resolvedOrganisation = React.useMemo(() => {
    if (organisationId) {
      const fromStore = organisations?.find((o) => o.uuid === organisationId);
      if (fromStore) return fromStore;
    }
    return organisation;
  }, [organisations, organisationId, organisation]);

  const taxRatesForConnectionSelector = React.useMemo(
    () => getTaxRatesForConnection(resolvedOrganisation?.connectionId),
    [resolvedOrganisation?.connectionId]
  );
  const taxRatesForConnection = useSelector(taxRatesForConnectionSelector);
  const preferencesHook = usePreferences();
  const currentUser = useSelector(getAuthUser);

  const coaSkippedPrefKey = organisationId
    ? `${RECEIPTS_BEFORE_SCAN_COA_SKIPPED_KEY_PREFIX}-${organisationId}`
    : null;

  const coaSkippedFromPref =
    coaSkippedPrefKey && currentUser?.userUuid
      ? preferencesHook(
          PreferenceActions.GET,
          PreferenceSections.TRANSACTIONS,
          { name: coaSkippedPrefKey },
          currentUser.userUuid
        ) === true
      : false;

  const coaSkipped = options?.coaSkipped ?? coaSkippedFromPref;

  return areReceiptsScanPrerequisitesMetForOrganisation(resolvedOrganisation, {
    coaSkipped,
    taxRatesFromStore: taxRatesForConnection,
  });
}
